import React, { useState, useEffect } from "react";
import { Button, Spin, Alert, Card, Result } from "antd";
import queryString from "query-string";
import logo from "@iso/assets/images/logo.png";
import { propertyCheckinService } from "@iso/services";
import PropertyCheckinLandingWrapper from "../PropertyCheckin/PropertyCheckinLanding.styles";

const SUCCESS_CODE = "0000";

const formatTime = (value) => {
  if (!value) {
    return "";
  }
  const d = new Date(String(value).replace(" ", "T"));
  return isNaN(d.getTime()) ? value : d.toLocaleString();
};

const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, "")}`;

// Big enough to hit first time on a phone at a gate.
const BIG_BUTTON = { height: 56, fontSize: 18 };

/**
 * Public page a division contact opens from the SMS link: confirms the
 * contractor really did make contact before starting work. The token in the
 * link is the only credential, so this works signed in or not.
 */
export default function DivisionContactConfirm() {
  const token = queryString.parse(window.location.search).token || "";
  const [loading, setLoading] = useState(!!token);
  const [data, setData] = useState(null);
  const [invalid, setInvalid] = useState(!token);
  const [loadError, setLoadError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  useEffect(() => {
    if (!token) {
      return;
    }
    setLoading(true);
    propertyCheckinService
      .getDivisionContact(token)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          setData(res);
        } else {
          setInvalid(true);
        }
      })
      .catch(() => setLoadError("We couldn't load this link. Check your connection and try again."))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = () => {
    if (confirming || !data || data.status === "verified") {
      return;
    }
    setConfirming(true);
    setConfirmError("");
    propertyCheckinService
      .confirmDivisionContact(token)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          setData((prev) => ({
            ...prev,
            status: res.status || "verified",
            verified_at: res.verified_at || prev.verified_at,
          }));
        } else {
          setConfirmError((res && res.message) || "We couldn't record that. Please try again.");
        }
      })
      .catch(() => setConfirmError("We couldn't record that. Please try again."))
      .finally(() => setConfirming(false));
  };

  const contractor = (data && data.contractor) || {};
  const who = contractor.business_name
    ? `${contractor.name || "A contractor"} (${contractor.business_name})`
    : contractor.name || "A contractor";
  const verified = data && data.status === "verified";
  // The main office gets this same page when a contractor couldn't reach their
  // division contact and was sent to them instead.
  const asOffice = !!(data && data.confirming_as === "office");

  return (
    <PropertyCheckinLandingWrapper className="isoSignInPage">
      <div className="isoLoginContentWrapper">
        <div className="isoLoginContent">
          <div className="isoLogoWrapper">
            <img src={logo} height="40" alt="AirAgri" />
          </div>

          {loading && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin size="large" />
            </div>
          )}

          {!loading && invalid && (
            <Result
              status="warning"
              title="This link is no longer valid"
              subTitle="It may have expired or already been replaced. You can also confirm contact from the Visitors page in AirAgri."
            />
          )}

          {!loading && !invalid && loadError && (
            <Alert type="error" showIcon message={loadError} />
          )}

          {!loading && data && (
            <div className="division-contact-confirm">
              <h2 className="welcome-title">Confirm contact</h2>
              <Card size="small" style={{ marginBottom: 16 }}>
                {asOffice ? (
                  <p style={{ fontSize: 16, marginBottom: 8 }}>
                    <strong>{who}</strong> could not reach their{" "}
                    <strong>{data.division_name || "division"}</strong> contact and has been sent to the
                    main office. Confirm once they have reported to you.
                  </p>
                ) : (
                  <p style={{ fontSize: 16, marginBottom: 8 }}>
                    <strong>{who}</strong> signed in for{" "}
                    <strong>{data.division_name || "your division"}</strong> at{" "}
                    {data.property_name || "the property"}
                    {data.checkin_time ? ` at ${formatTime(data.checkin_time)}` : ""}.
                  </p>
                )}
                {contractor.phone && (
                  <p style={{ marginBottom: 0 }}>
                    Phone: <a href={telHref(contractor.phone)}>{contractor.phone}</a>
                  </p>
                )}
              </Card>

              <div role="status" aria-live="polite">
                {verified ? (
                  <Result
                    status="success"
                    title={data.verified_at ? `Confirmed at ${formatTime(data.verified_at)}` : "Confirmed"}
                    subTitle={data.verified_by_name ? `Confirmed by ${data.verified_by_name}.` : "They can start work."}
                  />
                ) : (
                  confirmError && (
                    <Alert type="error" showIcon style={{ marginBottom: 16 }} message={confirmError} />
                  )
                )}
              </div>

              {!verified && (
                <>
                  <p style={{ marginBottom: 16 }}>
                    {asOffice
                      ? "Only confirm once they have reported to the main office."
                      : "Only confirm once you have actually spoken with them, in person or by phone."}
                  </p>
                  <Button
                    type="primary"
                    className="btn-success"
                    size="large"
                    block
                    style={BIG_BUTTON}
                    loading={confirming}
                    onClick={confirm}
                  >
                    {asOffice ? "Confirm they've reported in" : "Confirm contact made"}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </PropertyCheckinLandingWrapper>
  );
}
